import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Avatar, Badge } from '@heroui/react';
import { cn, formatDate } from '@/shared/lib/utils';
import type { NotificationResponse } from '@/entities/notification';

interface NotificationItemProps {
  notification: NotificationResponse;
  onMarkAsRead: (id: number) => void;
  className?: string;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  className,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    if (notification.link_url) {
      navigate(notification.link_url);
    }
  };

  return (
    <Card
      isPressable
      onPress={handleClick}
      className={cn(
        'p-4 rounded-xl hover:bg-default-100 hover:rounded-xl transition-colors',
        !notification.is_read && 'bg-primary/5',
        className
      )}
    >
      <div className="flex gap-3">
        {notification.image_url && (
          <Avatar
            src={notification.image_url}
            alt={notification.title}
            className="shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4
              className={cn(
                'text-sm line-clamp-1 text-left',
                !notification.is_read ? 'font-semibold' : 'font-medium'
              )}
            >
              {notification.title}
            </h4>
            {!notification.is_read && (
              <Badge color="primary" size="sm" isDot className="shrink-0">
                {' '}
              </Badge>
            )}
          </div>
          <p className="text-sm text-default-500 mt-1 line-clamp-2 text-left">
            {notification.content}
          </p>
          <span className="text-xs text-default-400 mt-2 block text-right">
            { formatDate(notification.created_at) }
          </span>
        </div>
      </div>
    </Card>
  );
};

NotificationItem.displayName = 'NotificationItem';
